import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import { decryptData } from "../../../services/crypto";
import { useGetTelesalesDashboardQuery } from "../../../features/telesalesdashboardSlice";
import { useGetAllNotificationsQuery } from "../../../features/notificationSlice";

// Import telesales-specific components
import { TelesalesKPIs } from "./TelesalesKPIs";
import { TelesalesProgressCharts } from "./TelesalesProgressCharts";
import { TelesalesNotifications } from "./TelesalesNotifications";

export const TelesalesDashboard = ({ showModal, setShowModal }) => {
  const [notificationData, setNotificationData] = useState([]);

  // Get user data
  let staffId = null;
  let storedStaffId = localStorage.getItem("id");
  let role = null;
  let storedStaffRole = localStorage.getItem("role");

  if (storedStaffId) {
    try {
      staffId = decryptData(storedStaffId);
    } catch (error) {
      console.error("Failed to decrypt staff ID:", error);
    }
  }

  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      console.error("Failed to decrypt role:", error);
    }
  }

  // API calls - Telesales specific dashboard
  const { data: dashboardData, isFetching } = useGetTelesalesDashboardQuery({ 
    employeeId: staffId 
  }, {
    skip: !staffId,
    pollingInterval: 60000 // Refresh every minute
  });
  
  const { data: notificationsData, isLoading: notificationsLoading } = useGetAllNotificationsQuery();

  // Process notifications
  useEffect(() => {
    if (notificationsData && Array.isArray(notificationsData) && notificationsData.length > 0) {
      setNotificationData(notificationsData);
    }
  }, [notificationsData]);


  return (
    <DashboardContainer>
      {/* Header */}
      <DashboardHeader>
        <HeaderContent>
          <WelcomeSection>
            <h1>Welcome back, Telesales Agent!</h1>
            <p>Here's your performance overview for today</p>
          </WelcomeSection>
          <NotificationBadge>
            {notificationData.length > 0 && (
              <NotificationCount>{notificationData.length}</NotificationCount>
            )}
            🔔
          </NotificationBadge>
        </HeaderContent>
      </DashboardHeader>

      {/* Main Content */}
      <MainContent>
        <UnifiedDashboard>
          <TelesalesKPIs dashboardData={dashboardData} isFetching={isFetching} />
          <TelesalesProgressCharts dashboardData={dashboardData} isFetching={isFetching} />
        </UnifiedDashboard>
      </MainContent>

      {/* Notifications Modal */}
      {showModal && notificationData.length > 0 && (
        <TelesalesNotifications
          notifications={notificationData}
          onClose={() => setShowModal(false)}
        />
      )}
    </DashboardContainer>
  );
};

// Styled Components
const DashboardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 2rem;
  margin-top: 80px;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-top: 70px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    margin-top: 60px;
  }
`;

const DashboardHeader = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const WelcomeSection = styled.div`
  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: ${colors.text};
    margin: 0 0 0.5rem 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    
    @media (max-width: 768px) {
      font-size: 1.75rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }
  
  p {
    color: #64748b;
    font-size: 1.1rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }
`;

const NotificationBadge = styled.div`
  position: relative;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  background: #f1f5f9;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e2e8f0;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
    padding: 0.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 0.35rem;
  }
`;

const NotificationCount = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
`;

const MainContent = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const UnifiedDashboard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;
